import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useMenuDismiss from './useMenuDismiss';
import { navPrograms, programAt, programName, PROGRAM_TABS, shownTabs } from './programs';

// The top bar: which program you are in, and which page of it you are on. Both are dropdowns
// rather than rows of tabs, because the row stopped fitting — TW4 has eight pages now, and the
// program list grows as the advanced aircraft arrive. Neither is a hamburger: each closed menu
// names where you are, so the bar still answers "what am I looking at" without being opened.

// The program list is shared with everything else that names a school: see programs.js.

// The tab you are on: the longest `to` the path starts with, so /primary/discuss/hud is still
// Discussion Items and /primary/systems/fuel is still Systems.
function currentTab(tabs, pathname) {
  return tabs
    .filter((t) => pathname === t.to || pathname.startsWith(`${t.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0] || null;
}

// A menu that closes on a choice, and (through useMenuDismiss) on Escape or a click elsewhere.
function Dropdown({ name, current, align = 'left', children }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const trigger = useRef(null);
  useMenuDismiss(open, setOpen, wrap, trigger);

  return (
    <div className={`topnav-menu topnav-menu--${align}`} ref={wrap}>
      <button
        type="button"
        ref={trigger}
        className="topnav-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${name}: ${current}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="topnav-current">{current}</span>
        <span className="topnav-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="topnav-panel" role="menu" onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

// The wordmark's full size, and the smallest it is shrunk to before it gives way altogether.
const BRAND_PX = 26;
const BRAND_MIN_PX = 11;
const BRAND_GAP = 10; // clear space kept between the wordmark and either menu

// Sizes the wordmark to the room the two menus leave it. It is centred on the bar, so what it
// may use is twice the distance from the centre to the nearer menu. Measured at full size and
// scaled down in proportion, which is exact because the letter-spacing is in em.
function useBrandFit(bar, brand, deps) {
  useLayoutEffect(() => {
    const el = bar.current;
    const mark = brand.current;
    if (!el || !mark) return undefined;
    const fit = () => {
      const menus = el.querySelectorAll('.topnav-menu');
      if (menus.length < 2) return;
      const box = el.getBoundingClientRect();
      const centre = box.left + box.width / 2;
      const room = 2 * Math.min(
        centre - menus[0].getBoundingClientRect().right,
        menus[1].getBoundingClientRect().left - centre,
      ) - 2 * BRAND_GAP;
      mark.style.fontSize = `${BRAND_PX}px`;
      const full = mark.getBoundingClientRect().width;
      const px = Math.min(BRAND_PX, (BRAND_PX * room) / full);
      mark.style.fontSize = `${Math.max(px, BRAND_MIN_PX)}px`;
      mark.style.visibility = px < BRAND_MIN_PX ? 'hidden' : '';
    };
    fit();
    const watch = new ResizeObserver(fit);
    watch.observe(el);
    el.querySelectorAll('.topnav-menu').forEach((m) => watch.observe(m));
    // The web font arriving changes every width measured here without resizing the bar.
    if (document.fonts) document.fonts.ready.then(fit);
    return () => watch.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function TopNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const bar = useRef(null);
  const brand = useRef(null);

  // The program whose pages these are, if they are served here: an unlisted one is (someone was
  // sent the link), so the bar names it and offers its tabs, though the menu does not list it.
  // A draft one on the live site is not, and the bar falls back to the first program listed.
  const offered = navPrograms();
  const program = programAt(pathname) || offered[0];
  const tabs = shownTabs(PROGRAM_TABS[program.id]);
  const tab = currentTab(tabs, pathname);
  useBrandFit(bar, brand, [program.id, tab && tab.to]);

  return (
    <div className="navbar" ref={bar}>
      <Dropdown name="Program" current={programName(program)}>
        {(offered.includes(program) ? offered : [...offered, program]).map((p) => (
          <button
            type="button"
            key={p.id}
            role="menuitem"
            className={`topnav-item${p.id === program.id ? ' active' : ''}`}
            onClick={() => navigate(p.home)}
          >
            {programName(p)}
          </button>
        ))}
      </Dropdown>

      {/* The P carries its own letter-spacing: see .topnav-brand-p for why the even one is wrong. */}
      <Link to="/" className="topnav-brand" ref={brand} aria-label="pinksheetmafia.com home">
        <span className="topnav-brand-p">P</span>SM
      </Link>

      <Dropdown name="Page" current={tab ? tab.label : 'Menu'} align="right">
        {tabs.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            role="menuitem"
            className={`topnav-item${tab && t.to === tab.to ? ' active' : ''}`}
          >
            {t.label}
          </Link>
        ))}
      </Dropdown>
    </div>
  );
}

export default TopNav;
